/**
 * The retake column: "89d 7h", live, in a table cell.
 *
 * A separate component from RetakeCountdown because it has a different job. The countdown is a
 * panel somebody reads; this is a figure somebody scans down a column. Sharing the arithmetic
 * (both call retakeTimeRemaining) while keeping the presentation apart is what stops the table
 * either growing a three-line block in every row or the panel shrinking to a shorthand nobody
 * can act on.
 *
 * It owns its own tick rather than taking `now` from the page. A single clock lifted to the
 * parent would re-render the entire table every minute to update one column; per-cell state
 * re-renders only the cells that change. There are at most a handful of failed certifications
 * per employee, so the cost is trivial and the blast radius is one cell.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  formatRetakeRemainingCompact,
  retakeTimeRemaining,
  retakeUrgency,
} from '../../types/certification';
import './Certifications.css';

interface Props {
  deadline?: string;
}

const RetakeCell: React.FC<Props> = ({ deadline }) => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!deadline) return;
    let timeoutId: number | undefined;
    let intervalId: number | undefined;

    // Aligned to the next whole minute, then a steady beat — see RetakeCountdown.
    const msToNextMinute = 60000 - (Date.now() % 60000);
    timeoutId = window.setTimeout(() => {
      setNow(new Date());
      intervalId = window.setInterval(() => setNow(new Date()), 60000);
    }, msToNextMinute);

    return () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [deadline]);

  const remaining = useMemo(() => retakeTimeRemaining(deadline, now), [deadline, now]);

  // A passed certification has no deadline, and a dash is the honest rendering of "not
  // applicable" — clearer than an empty cell, which reads as missing data.
  if (!remaining) return <span className="certs__muted">—</span>;

  return (
    <span className={`cert-retake-cell is-${retakeUrgency(remaining)}`}>
      {formatRetakeRemainingCompact(remaining)}
    </span>
  );
};

export default RetakeCell;
